import { useState, useEffect, FormEvent } from 'react'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
    MdSearch,
    MdFilterList,
    MdChat,
    MdShoppingCart,
    MdInventory,
    MdBusiness,
    MdSupportAgent,
    MdCalendarToday,
    MdLocalHospital,
    MdAssignment,
    MdBiotech,
    MdDescription,
    MdMedicalServices,
    MdPerson,
    MdBadge,
    MdList,
    MdMeetingRoom,
    MdFolderOpen,
    MdPriceCheck,
    MdReceipt,
    MdPayment,
    MdMedication,
    MdLocalPharmacy,
    MdThermostat,
    MdBed,
    MdApartment,
    MdHotel,
    MdCleaningServices,
    MdMonitorHeart,
    MdWaterDrop,
    MdEditNote,
    MdExitToApp,
    MdArrowForward,
    MdExpandMore,
    MdExpandLess,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    searchGlobal,
    SearchModule,
    SearchResultItem,
    GlobalSearchResult,
    getModuleConfig,
} from '@/lib/api/global-search/global-search-api'

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
    MdChat,
    MdShoppingCart,
    MdInventory,
    MdBusiness,
    MdSupportAgent,
    MdCalendarToday,
    MdLocalHospital,
    MdAssignment,
    MdBiotech,
    MdDescription,
    MdMedicalServices,
    MdPerson,
    MdBadge,
    MdList,
    MdMeetingRoom,
    MdFolderOpen,
    MdPriceCheck,
    MdReceipt,
    MdPayment,
    MdMedication,
    MdLocalPharmacy,
    MdThermostat,
    MdBed,
    MdApartment,
    MdHotel,
    MdCleaningServices,
    MdMonitorHeart,
    MdWaterDrop,
    MdEditNote,
    MdExitToApp,
}

export default function SearchResultsPage() {
    const moduleConfig = getModuleConfig(useServiceLabel())
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()

    const [query, setQuery] = useState(searchParams.get('q') || '')
    const [results, setResults] = useState<GlobalSearchResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [expandedModules, setExpandedModules] = useState<Set<string>>(
        new Set()
    )
    const [selectedModules, setSelectedModules] = useState<Set<SearchModule>>(
        new Set()
    )
    const [showFilters, setShowFilters] = useState(false)

    // Buscar ao carregar ou mudar query param
    useEffect(() => {
        const q = searchParams.get('q')
        if (q && q.length >= 2) {
            setQuery(q)
            performSearch(q)
        }
    }, [searchParams])

    const performSearch = async (searchQuery: string) => {
        if (searchQuery.length < 2) return

        setLoading(true)
        try {
            const modulesFilter =
                selectedModules.size > 0
                    ? Array.from(selectedModules)
                    : undefined

            const response = await searchGlobal({
                query: searchQuery,
                limit: 20,
                fullSearch: true,
                modules: modulesFilter,
            })

            if (response.success) {
                setResults(response.result)
                // Expandir todos os modulos com resultados
                const modulesWithResults = Object.keys(
                    response.result?.resultsByModule || {}
                )
                setExpandedModules(new Set(modulesWithResults))
            } else {
                toast.error(response.error || 'Erro ao buscar')
            }
        } catch (error) {
            console.error('Erro na busca:', error)
            toast.error('Erro ao realizar busca')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e: FormEvent) => {
        e.preventDefault()
        if (query.length >= 2) {
            setSearchParams({ q: query })
            performSearch(query)
        }
    }

    const toggleModule = (module: string) => {
        const newExpanded = new Set(expandedModules)
        if (newExpanded.has(module)) {
            newExpanded.delete(module)
        } else {
            newExpanded.add(module)
        }
        setExpandedModules(newExpanded)
    }

    const toggleModuleFilter = (module: SearchModule) => {
        const newSelected = new Set(selectedModules)
        if (newSelected.has(module)) {
            newSelected.delete(module)
        } else {
            newSelected.add(module)
        }
        setSelectedModules(newSelected)
    }

    const handleApplyFilters = () => {
        if (query.length >= 2) {
            performSearch(query)
        }
        setShowFilters(false)
    }

    const clearFilters = () => {
        setSelectedModules(new Set())
        if (query.length >= 2) {
            performSearch(query)
        }
    }

    const handleItemClick = (item: SearchResultItem) => {
        navigate(item.detailUrl)
    }

    const getIcon = (iconName: string) => {
        const IconComponent = ICON_MAP[iconName]
        return IconComponent ? <IconComponent className="text-lg" /> : null
    }

    const getModuleColor = (module: SearchModule): string => {
        return moduleConfig[module]?.color || 'badge-ghost'
    }

    const resultsByModule = results?.resultsByModule || {}
    const totalResults = results?.totalResults || 0

    return (
        <div className="p-4 md:p-6">
            {/* Cabecalho */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-base-content mb-2">
                    Busca Global
                </h1>
                <p className="text-base-content/70">
                    Pesquise em todos os modulos do sistema
                </p>
            </div>

            {/* Formulario de busca */}
            <form onSubmit={handleSearch} className="mb-6">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Digite para buscar..."
                            className="input input-bordered w-full pl-10"
                        />
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl" />
                    </div>
                    <button
                        type="submit"
                        disabled={query.length < 2 || loading}
                        className="btn btn-secondary"
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSearch className="text-xl" />
                        )}
                        <span className="hidden sm:inline">Buscar</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn ${selectedModules.size > 0 ? 'btn-secondary' : 'btn-ghost'}`}
                    >
                        <MdFilterList className="text-xl" />
                        {selectedModules.size > 0 && (
                            <span className="badge badge-sm">
                                {selectedModules.size}
                            </span>
                        )}
                    </button>
                </div>
            </form>

            {/* Filtros por modulo */}
            {showFilters && (
                <div className="card bg-base-200 mb-6">
                    <div className="card-body p-4">
                        <h3 className="font-semibold mb-3">
                            Filtrar por modulo
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {Object.values(SearchModule).map(module => (
                                <button
                                    key={module}
                                    type="button"
                                    onClick={() => toggleModuleFilter(module)}
                                    className={`badge badge-lg cursor-pointer ${
                                        selectedModules.has(module)
                                            ? getModuleColor(module)
                                            : 'badge-outline'
                                    }`}
                                >
                                    {getIcon(moduleConfig[module]?.icon)}
                                    <span className="ml-1">
                                        {moduleConfig[module]?.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleApplyFilters}
                                className="btn btn-secondary btn-sm"
                            >
                                Aplicar Filtros
                            </button>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="btn btn-ghost btn-sm"
                            >
                                Limpar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Resultados */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : results ? (
                <>
                    {/* Resumo */}
                    <div className="mb-4 text-base-content/70">
                        {totalResults} resultado{totalResults !== 1 ? 's' : ''}{' '}
                        encontrado{totalResults !== 1 ? 's' : ''} para "
                        {results.query}"
                    </div>

                    {/* Lista de resultados agrupados por modulo */}
                    {totalResults > 0 ? (
                        <div className="space-y-4">
                            {Object.entries(resultsByModule).map(
                                ([module, items]) => (
                                    <div
                                        key={module}
                                        className="card bg-base-100 shadow-sm border border-base-300"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleModule(module)}
                                            className="w-full p-4 flex items-center justify-between hover:bg-base-200/50 transition-colors rounded-t-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span
                                                    className={`badge ${getModuleColor(module as SearchModule)}`}
                                                >
                                                    {getIcon(
                                                        moduleConfig[
                                                            module as SearchModule
                                                        ]?.icon
                                                    )}
                                                </span>
                                                <span className="font-semibold">
                                                    {
                                                        moduleConfig[
                                                            module as SearchModule
                                                        ]?.label
                                                    }
                                                </span>
                                                <span className="badge badge-ghost badge-sm">
                                                    {items?.length || 0}
                                                </span>
                                            </div>
                                            {expandedModules.has(module) ? (
                                                <MdExpandLess className="text-xl text-base-content/50" />
                                            ) : (
                                                <MdExpandMore className="text-xl text-base-content/50" />
                                            )}
                                        </button>

                                        {expandedModules.has(module) &&
                                            items && (
                                                <div className="border-t border-base-300">
                                                    {items.map(item => (
                                                        <button
                                                            key={`${item.module}-${item.id}`}
                                                            type="button"
                                                            onClick={() =>
                                                                handleItemClick(
                                                                    item
                                                                )
                                                            }
                                                            className="w-full p-4 flex items-center gap-4 hover:bg-base-200/50 transition-colors border-b border-base-200 last:border-b-0 text-left"
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-base-content truncate">
                                                                    {item.title}
                                                                </p>
                                                                {item.subtitle && (
                                                                    <p className="text-sm text-base-content/70 truncate">
                                                                        {
                                                                            item.subtitle
                                                                        }
                                                                    </p>
                                                                )}
                                                                {item.description && (
                                                                    <p className="text-sm text-base-content/50 truncate">
                                                                        {
                                                                            item.description
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <MdArrowForward className="text-xl text-base-content/30 shrink-0" />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <MdSearch className="text-6xl text-base-content/20 mx-auto mb-4" />
                            <p className="text-base-content/60">
                                Nenhum resultado encontrado para "
                                {results.query}"
                            </p>
                            <p className="text-base-content/40 text-sm mt-2">
                                Tente usar outros termos ou remover os filtros
                            </p>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-12">
                    <MdSearch className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <p className="text-base-content/60">
                        Digite ao menos 2 caracteres para buscar
                    </p>
                </div>
            )}
        </div>
    )
}
